import { c as _c } from "react/compiler-runtime";
import { add, setMilliseconds, startOfHour, sub } from 'date-fns';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import NumberFormat from 'react-number-format';
import { useTheme } from 'styled-components';
import { RequestView } from '../../../../../../../typings/View.interface';
import LINKS from '../../../../configs/links';
import { RequestListFiltersProps, RequestListQueryParams } from '../../../../typings/RequestList.interface';
import { deepMergeFilters, extractFromArray, toArray } from '../../../../utils';
import { RelativeDate, resolveRelativeDate } from '../../../../utils/date';
import formatMetricsQuery from '../../../../utils/formatMetricsQuery';
import Badge from '../../../common/base/Badge';
import Button, { ButtonGroup } from '../../../common/base/Button';
import Divider from '../../../common/base/Divider';
import Icon from '../../../common/base/Icon';
import Skeleton from '../../../common/base/Skeleton';
import { StatusDot } from '../../../common/base/StatusDot';
import Text from '../../../common/base/Text';
import Tooltip from '../../../common/base/Tooltip';
import { BulkOperationFilters, BulkRequestActionDropdown } from '../../../common/BulkOperations';
import EmptyState, { EmptyResults, StyledEmptyStateWrapper } from '../../../common/EmptyState';
import { date_filter_component, FilterFormValues, request_list_filters as filter_components } from '../../../common/Filters/FilterComponents';
import Filters, { FiltersContext } from '../../../common/Filters/Filters';
import ErrorBoundary from '../../../common/helpers/ErrorBoundary';
import { StyledCentered } from '../../../common/helpers/StyledCentered';
import { Div } from '../../../common/helpers/StyledUtils';
import Search from '../../../common/Search';
import TableFooter from '../../../common/Table/TableFooter';
import { useToasts } from '../../../common/Toast';
import { GlobalContext } from '../../../contexts/GlobalContext';
import { TableProvider } from '../../../contexts/TableContext';
import useHasSlowItemRate from '../../../hooks/useHasSlowItemRate';
import { useIsScrolled } from '../../../hooks/useIsScrolled';
import useMetricsQuery from '../../../hooks/useMetricsQuery';
import { useNextRelativeDate } from '../../../hooks/useNextRelativeDate';
import useRequestList from '../../../hooks/useRequestList';
import useSearchQuery from '../../../hooks/useSearchQuery';
import { StyledViewContent, StyledViewLayout, StyledViewNav, StyledViewNavActions, StyledViewNavHeader, StyledViewNavSecondary, ViewTitle } from '../../../layouts/ViewLayout';
import { DashboardContext } from '../DashboardContext';
import Histogram, { DateFilter } from '../Events/Histogram';
import useRequestEntriesList from '../Request/useRequestEntriesList';
import { ViewsContext } from '../ViewsContext';
import EntrySidebar from './EntrySidebar';
import RequestSidebar from './RequestSidebar';
import RequestsList from './RequestsList';
import SuggestedColumns from './SuggestedColumns';
export const extractFiltersFromQuery = (parsed_query: RequestListQueryParams): RequestListFiltersProps => {
  let date = extractFromArray(parsed_query.date);
  if (typeof date === 'string') {
    const {
      relative,
      min,
      max
    } = resolveRelativeDate(date);
    date = {
      relative,
      min: min?.toISOString(),
      max: max?.toISOString()
    };
  } else {
    const parsed_query_date = parsed_query.date as {
      max?: string | string[];
      min?: string | string[];
    };
    date = {
      max: extractFromArray(parsed_query_date?.max) as string,
      min: extractFromArray(parsed_query_date?.min) as string
    };
  }
  return {
    date,
    search_term: extractFromArray(parsed_query.search_term) as string,
    bulk_retry_id: toArray(parsed_query.bulk_retry_id || []) as string[],
    source_id: toArray(parsed_query.source_id || []) as string[],
    request: {
      headers: extractFromArray(parsed_query.request?.headers) as string,
      body: extractFromArray(parsed_query.request?.body) as string,
      path: extractFromArray(parsed_query.request?.path) as string,
      parsed_query: extractFromArray(parsed_query.request?.parsed_query) as string
    },
    ignored_count: {
      max: extractFromArray(parsed_query.ignored_count?.max) as string,
      min: extractFromArray(parsed_query.ignored_count?.min) as string
    },
    events_count: {
      max: extractFromArray(parsed_query.events_count?.max) as string,
      min: extractFromArray(parsed_query.events_count?.min) as string
    },
    cli_events_count: {
      max: extractFromArray(parsed_query.cli_events_count?.max) as string,
      min: extractFromArray(parsed_query.cli_events_count?.min) as string
    },
    status: parsed_query.status,
    rejection_cause: toArray(parsed_query.rejection_cause || []) as string[],
    next: extractFromArray(parsed_query.next) as string,
    prev: extractFromArray(parsed_query.prev) as string
  };
};
const extractFiltersFromView = (request_view: RequestView): RequestListFiltersProps => {
  const filters = request_view.filters;
  return {
    ...filters,
    date: {
      relative: extractFromArray(filters.date?.relative) as RelativeDate,
      max: extractFromArray(filters.date?.max) as string,
      min: extractFromArray(filters.date?.min) as string
    }
  };
};
const extractFiltersForList = (filters: RequestListFiltersProps): RequestListFiltersProps => {
  if (filters.date?.relative) {
    const {
      min,
      max
    } = resolveRelativeDate(filters.date.relative);
    return {
      ...filters,
      date: {
        min: min.toISOString(),
        max: max.toISOString()
      }
    };
  }
  return filters;
};
const DEFAULT_DATE = 'yesterday';
const MIN_SEARCH_TERM_LENGTH = 3;
const normalizeSearchTerm = (search_term?: string) => {
  if (!search_term || search_term.length < MIN_SEARCH_TERM_LENGTH) {
    return undefined;
  }
  return search_term;
};
const filters_initial_state = extractFiltersFromQuery({
  date: DEFAULT_DATE
});

// Hoisted out of RequestsView: resolves a relative date filter into a concrete stats window.
// Must never mutate `filters` — writing the resolved min/max back onto filters.date gives
// every consumer of `filters` new content each render, which cascades into a formik update
// loop ("Maximum update depth exceeded") in the filters sync.
const computeStatsDateRange = (filters: RequestListFiltersProps): [Date, Date] => {
  const now = new Date();
  let date_min = filters.date?.min;
  let date_max = filters.date?.max;
  if (filters.date?.relative) {
    const {
      min,
      max
    } = resolveRelativeDate(filters.date.relative);
    date_min = min.toISOString();
    date_max = max.toISOString();
  }
  const end_date = setMilliseconds(date_max ? new Date(date_max) : add(startOfHour(now), {
    hours: 1
  }), 0);
  const start_date = setMilliseconds(date_min ? new Date(date_min) : sub(add(startOfHour(now), {
    hours: 1
  }), {
    days: 1
  }), 0);
  return [start_date, end_date];
};
const RequestsView: React.FC<{
  current_view?: RequestView;
}> = t0 => {
  const $ = _c(186);
  const {
    current_view
  } = t0;
  const theme = useTheme();
  const {
    addToast
  } = useToasts();
  const {
    query,
    updateSearchQuery
  } = useSearchQuery();
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const {
    subscription,
    has_sources
  } = useContext(DashboardContext);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = Date.now();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [list_render_key, setRenderKey] = useState(t1);
  const [ignore_current_view, setIgnoreCurrentView] = useState(false);
  const current_page = query.current_page ? parseInt(query.current_page, 10) : 1;
  const {
    has_scrolled,
    scrolled_element_ref
  } = useIsScrolled();
  const search_input_ref = useRef(null);
  const {
    views,
    createView,
    updateView,
    deleteView,
    renameView,
    duplicateView
  } = useContext(ViewsContext);
  let t2;
  if ($[1] !== query.selected_request_id) {
    t2 = extractFromArray(query.selected_request_id);
    $[1] = query.selected_request_id;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const selected_request_id = t2 as string;
  let t3;
  if ($[3] !== query.selected_entry_id) {
    t3 = extractFromArray(query.selected_entry_id);
    $[3] = query.selected_entry_id;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const selected_entry_id = t3 as string;
  let new_filters;
  if ($[5] !== query) {
    new_filters = extractFiltersFromQuery(query);
    if (!query.date) {
      let t4;
      let t5;
      let t6;
      if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
        const {
          min,
          max
        } = resolveRelativeDate(DEFAULT_DATE);
        t4 = DEFAULT_DATE;
        t5 = min?.toISOString();
        t6 = max?.toISOString();
        $[7] = t4;
        $[8] = t5;
        $[9] = t6;
      } else {
        t4 = $[7];
        t5 = $[8];
        t6 = $[9];
      }
      let t7;
      if ($[10] !== t5 || $[11] !== t6) {
        t7 = {
          relative: t4,
          min: t5,
          max: t6
        };
        $[10] = t5;
        $[11] = t6;
        $[12] = t7;
      } else {
        t7 = $[12];
      }
      new_filters.date = t7;
    }
    $[5] = query;
    $[6] = new_filters;
  } else {
    new_filters = $[6];
  }
  if (current_view && !ignore_current_view) {
    const should_use_query = Object.values(filter_components).some(v => query[v.filter_key] !== undefined);
    if (!should_use_query) {
      let t4;
      if ($[13] !== current_view) {
        t4 = extractFiltersFromView(current_view);
        $[13] = current_view;
        $[14] = t4;
      } else {
        t4 = $[14];
      }
      const view_filters = t4;
      let t5;
      if ($[15] !== new_filters.next || $[16] !== new_filters.prev || $[17] !== view_filters) {
        t5 = {
          ...filters_initial_state,
          ...view_filters,
          next: new_filters.next,
          prev: new_filters.prev
        };
        $[15] = new_filters.next;
        $[16] = new_filters.prev;
        $[17] = view_filters;
        $[18] = t5;
      } else {
        t5 = $[18];
      }
      new_filters = t5;
    }
  }
  const filters = new_filters;
  const [search_term_input, setSearchTermInput] = useState(filters.search_term || "");
  let t4;
  let t5;
  if ($[19] !== filters.search_term) {
    t4 = () => {
      setSearchTermInput(filters.search_term || "");
    };
    t5 = [filters.search_term];
    $[19] = filters.search_term;
    $[20] = t4;
    $[21] = t5;
  } else {
    t4 = $[20];
    t5 = $[21];
  }
  useEffect(t4, t5);
  const applied_filters = {
    ...filters,
    search_term: normalizeSearchTerm(filters.search_term)
  };
  const {
    next_relative_date
  } = useNextRelativeDate(filters, subscription);
  let t6;
  bb0: {
    if ("dynamic_columns" in query) {
      t6 = query.dynamic_columns === null ? undefined : query.dynamic_columns;
      break bb0;
    }
    if (current_view && !ignore_current_view && current_view.dynamic_columns) {
      t6 = current_view.dynamic_columns;
      break bb0;
    }
    t6 = undefined;
  }
  const dynamic_columns = t6;
  const t7 = filters.date?.relative;
  let t8;
  if ($[22] !== t7) {
    t8 = {
      include_data: true,
      type: "requests",
      date_filter_relative: t7
    };
    $[22] = t7;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  const list_data = useRequestList(extractFiltersForList(applied_filters), t8);
  const latest_request = list_data.requests.length > 0 && list_data.requests[0];
  const has_slow_request_rate = useHasSlowItemRate(list_data.requests);
  const [stats_start_date, stats_end_date] = computeStatsDateRange(filters);
  const query_params = {
    filters: {
      ...applied_filters,
      date: {
        min: stats_start_date.toISOString(),
        max: stats_end_date.toISOString()
      }
    }
  };
  const metrics_query = formatMetricsQuery("Requests", query_params);
  let t9;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      resetResultSetOnChange: false
    };
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  const {
    raw_data,
    is_loading: is_loading_metrics,
    refetch
  } = useMetricsQuery(metrics_query, t9);
  const has_request_filter = Object.values(applied_filters.request || {}).some(_temp) || applied_filters.search_term;
  const total_count = raw_data && raw_data.length > 0 ? raw_data[0]["Requests.count"] : null;
  let t10;
  if ($[25] !== updateSearchQuery) {
    t10 = selected_request_id_0 => updateSearchQuery({
      selected_request_id: selected_request_id_0
    }, {
      remove_keys: ["selected_entry_id"]
    });
    $[25] = updateSearchQuery;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  const onRequestSelected = t10;
  let t11;
  if ($[27] !== updateSearchQuery) {
    t11 = selected_entry_id_0 => updateSearchQuery({
      selected_entry_id: selected_entry_id_0
    });
    $[27] = updateSearchQuery;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  const onEntrySelected = t11;
  let t12;
  if ($[29] !== HookdeckAPI.track || $[30] !== current_view || $[31] !== dynamic_columns || $[32] !== filters.date || $[33] !== filters.search_term || $[34] !== selected_request_id || $[35] !== updateSearchQuery) {
    t12 = new_filters_0 => {
      HookdeckAPI.track.event("Filtered Request List", new_filters_0);
      if (current_view) {
        setIgnoreCurrentView(true);
      }
      updateSearchQuery({
        ...new_filters_0,
        search_term: filters.search_term,
        date: filters.date?.relative ? filters.date.relative : filters.date,
        selected_request_id,
        dynamic_columns,
        current_page: undefined
      }, {
        remove_keys: ["selected_entry_id", "next", "prev", "current_page"],
        replace: true
      });
    };
    $[29] = HookdeckAPI.track;
    $[30] = current_view;
    $[31] = dynamic_columns;
    $[32] = filters.date;
    $[33] = filters.search_term;
    $[34] = selected_request_id;
    $[35] = updateSearchQuery;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  const onFilterChanged = t12;
  let t13;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = search_term => {
      setSearchTermInput(search_term);
    };
    $[37] = t13;
  } else {
    t13 = $[37];
  }
  const onSearchTermInputChange = t13;
  let t14;
  if ($[38] !== current_view || $[39] !== dynamic_columns || $[40] !== filters || $[41] !== selected_request_id || $[42] !== updateSearchQuery) {
    t14 = search_term_0 => {
      if (current_view) {
        setIgnoreCurrentView(true);
      }
      search_input_ref?.current?.blur();
      updateSearchQuery({
        ...filters,
        search_term: normalizeSearchTerm(search_term_0),
        date: filters.date?.relative ? filters.date.relative : filters.date,
        selected_request_id,
        dynamic_columns,
        current_page: undefined
      }, {
        remove_keys: ["selected_entry_id", "next", "prev", "current_page"],
        replace: true
      });
    };
    $[38] = current_view;
    $[39] = dynamic_columns;
    $[40] = filters;
    $[41] = selected_request_id;
    $[42] = updateSearchQuery;
    $[43] = t14;
  } else {
    t14 = $[43];
  }
  const submitSearchTerm = t14;
  let t15;
  if ($[44] !== updateSearchQuery) {
    t15 = new_filters_1 => {
      const pagination = {
        next: new_filters_1.next,
        prev: new_filters_1.prev
      };
      updateSearchQuery({
        ...pagination
      }, {
        remove_keys: ["selected_entry_id", "selected_request_id"]
      });
    };
    $[44] = updateSearchQuery;
    $[45] = t15;
  } else {
    t15 = $[45];
  }
  const onPaginationChanged = t15;
  let t16;
  if ($[46] !== filters || $[47] !== updateSearchQuery) {
    t16 = date => {
      if (!date.relative) {
        delete date.relative;
      }
      updateSearchQuery({
        ...filters,
        date: date.relative ? date.relative : date
      }, {
        remove_keys: ["next", "prev", "current_page"]
      });
    };
    $[46] = filters;
    $[47] = updateSearchQuery;
    $[48] = t16;
  } else {
    t16 = $[48];
  }
  const setDate = t16;
  let t17;
  if ($[49] !== createView || $[50] !== dynamic_columns || $[51] !== filters.date) {
    t17 = values => {
      let view_filters_0 = filter_components.reduce((object, t18) => {
        const {
          form_name,
          formatForDB,
          isActive
        } = t18;
        if (!isActive(values[form_name])) {
          return object;
        }
        const formatted = formatForDB(values[form_name]);
        if (!formatted) {
          return object;
        }
        return deepMergeFilters(object, formatted);
      }, {});
      if (filters.date && date_filter_component.isActive(filters.date)) {
        const formatted_date = date_filter_component.formatForDB(filters.date);
        if (formatted_date) {
          view_filters_0 = deepMergeFilters(view_filters_0, formatted_date);
        }
      }
      createView(view_filters_0, dynamic_columns || []);
    };
    $[49] = createView;
    $[50] = dynamic_columns;
    $[51] = filters.date;
    $[52] = t17;
  } else {
    t17 = $[52];
  }
  const handleCreateViewWithValues = t17;
  let t18;
  if ($[53] !== current_view || $[54] !== dynamic_columns || $[55] !== filters.date || $[56] !== updateView) {
    t18 = values_0 => {
      let view_filters_1 = filter_components.reduce((object_0, t19) => {
        const {
          form_name: form_name_0,
          formatForDB: formatForDB_0,
          isActive: isActive_0
        } = t19;
        if (!isActive_0(values_0[form_name_0])) {
          return object_0;
        }
        const formatted_0 = formatForDB_0(values_0[form_name_0]);
        if (!formatted_0) {
          return object_0;
        }
        return deepMergeFilters(object_0, formatted_0);
      }, {});
      if (filters.date && date_filter_component.isActive(filters.date)) {
        const formatted_date_0 = date_filter_component.formatForDB(filters.date);
        if (formatted_date_0) {
          view_filters_1 = deepMergeFilters(view_filters_1, formatted_date_0);
        }
      }
      updateView(current_view.id, view_filters_1, dynamic_columns || []);
    };
    $[53] = current_view;
    $[54] = dynamic_columns;
    $[55] = filters.date;
    $[56] = updateView;
    $[57] = t18;
  } else {
    t18 = $[57];
  }
  const handleUpdateViewWithValues = t18;
  const handleRetry = (request_id, webhook_ids) => HookdeckAPI.requests.retry(request_id, webhook_ids).then(t19 => {
    const {
      request,
      events
    } = t19;
    addToast("success", `Request has been retried and ${events.length} events were created.`);
    list_data.refresh(request);
    entries_list.revalidate(events);
    return {
      request,
      events
    };
  }).catch(e => {
    addToast("error", "Request could not be retried");
    throw e;
  });
  let t20;
  if ($[58] !== list_data.requests || $[59] !== selected_request_id || $[60] !== updateSearchQuery) {
    t20 = () => {
      const selected_request_index = list_data.requests.findIndex(r => r.id === selected_request_id);
      const previous_request_index = selected_request_index - 1;
      const previous_request = list_data.requests[previous_request_index];
      if (previous_request) {
        updateSearchQuery({
          selected_request_id: previous_request.id
        });
      }
    };
    $[58] = list_data.requests;
    $[59] = selected_request_id;
    $[60] = updateSearchQuery;
    $[61] = t20;
  } else {
    t20 = $[61];
  }
  const handleOnPrev = t20;
  let t21;
  if ($[62] !== list_data.requests || $[63] !== selected_request_id || $[64] !== updateSearchQuery) {
    t21 = () => {
      const selected_request_index_0 = list_data.requests.findIndex(r_0 => r_0.id === selected_request_id);
      const next_request_index = selected_request_index_0 + 1;
      const next_request = list_data.requests[next_request_index];
      if (next_request) {
        updateSearchQuery({
          selected_request_id: next_request.id
        });
      }
    };
    $[62] = list_data.requests;
    $[63] = selected_request_id;
    $[64] = updateSearchQuery;
    $[65] = t21;
  } else {
    t21 = $[65];
  }
  const handleOnNext = t21;
  let t22;
  if ($[66] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = () => {
      setIgnoreCurrentView(false);
    };
    $[66] = t22;
  } else {
    t22 = $[66];
  }
  let t23;
  if ($[67] !== current_view) {
    t23 = [current_view];
    $[67] = current_view;
    $[68] = t23;
  } else {
    t23 = $[68];
  }
  useEffect(t22, t23);
  let t24;
  if ($[69] !== list_data.requests || $[70] !== selected_request_id) {
    t24 = selected_request_id ? list_data.requests && list_data.requests.find(request_0 => request_0.id === selected_request_id) : undefined;
    $[69] = list_data.requests;
    $[70] = selected_request_id;
    $[71] = t24;
  } else {
    t24 = $[71];
  }
  const request_1 = t24;
  const entries_list = useRequestEntriesList(request_1, 10);
  let t25;
  if ($[72] !== current_view || $[73] !== deleteView || $[74] !== duplicateView || $[75] !== dynamic_columns || $[76] !== filters.date || $[77] !== handleCreateViewWithValues || $[78] !== handleUpdateViewWithValues || $[79] !== renameView || $[80] !== views) {
    t25 = t26 => {
      const {
        values: values_1
      } = t26;
      const filters_active_count_from_values = filter_components.reduce((count, f) => {
        if (f.isActive(values_1[f.form_name])) {
          count++;
        }
        return count;
      }, 0);
      const has_active_filters = filters_active_count_from_values > 0 || filters.date && date_filter_component.isActive(filters.date) && filters.date.relative !== DEFAULT_DATE || dynamic_columns && dynamic_columns.length > 0;
      if (!current_view) {
        if (views && views?.count > 249) {
          return <Tooltip align="right" tooltip="Views limit reached"><Button disabled={true} outline={true} icon="save">Save view</Button></Tooltip>;
        }
        return <Button outline={true} disabled={!has_active_filters} onClick={() => {
          handleCreateViewWithValues(values_1);
        }} icon="save">Save view</Button>;
      }
      return <ButtonGroup small={true} more_options={[{
        label: "Save as new view",
        disabled: views && views.count > 249,
        tooltip: views && views.count > 249 ? "Views limit reached" : undefined,
        onClick: () => {
          handleCreateViewWithValues(values_1);
        }
      }, {
        label: "Rename view",
        onClick: () => renameView(current_view.id)
      }, {
        label: "Duplicate view",
        onClick: () => duplicateView(current_view.id)
      }, {
        label: "Delete view",
        danger: true,
        onClick: () => {
          deleteView(current_view.id);
        }
      }]}><Button outline={true} onClick={() => {
          handleUpdateViewWithValues(values_1);
        }} disabled={!has_active_filters} icon="save">Save view</Button></ButtonGroup>;
    };
    $[72] = current_view;
    $[73] = deleteView;
    $[74] = duplicateView;
    $[75] = dynamic_columns;
    $[76] = filters.date;
    $[77] = handleCreateViewWithValues;
    $[78] = handleUpdateViewWithValues;
    $[79] = renameView;
    $[80] = views;
    $[81] = t25;
  } else {
    t25 = $[81];
  }
  const SaveViewButton = t25;
  const active_table = !(!has_sources && !list_data.requests.length && list_data.fetched) && list_data.requests.length > 0;
  const should_show_list_loading = list_data.is_loading && list_data.count === 0;
  let t26;
  if ($[82] !== dynamic_columns) {
    t26 = {
      dynamic_columns
    };
    $[82] = dynamic_columns;
    $[83] = t26;
  } else {
    t26 = $[83];
  }
  let t27;
  let t28;
  if ($[84] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = {
      align: "center",
      justify: "space-between",
      gap: 2
    };
    t28 = <ViewTitle title="Requests" tooltip={"Requests represent inbound HTTP requests to Hookdeck from external services (sources). Events \u2014 outbound requests to a destination \u2014 are derived from requests, based on the connection configuration(s) associated with the request source."} link={LINKS.product_docs.requests} />;
    $[84] = t27;
    $[85] = t28;
  } else {
    t27 = $[84];
    t28 = $[85];
  }
  let t29;
  if ($[86] !== has_request_filter || $[87] !== total_count) {
    t29 = <Div flex={t27}>{t28}{!total_count && !has_request_filter ? <Skeleton variant="square" h={{
        px: 20
      }} /> : <Badge neutral={true}>{has_request_filter ? "\xD8" : <NumberFormat renderText={_temp2} displayType="text" value={total_count} thousandSeparator="," />}</Badge>}</Div>;
    $[86] = has_request_filter;
    $[87] = total_count;
    $[88] = t29;
  } else {
    t29 = $[88];
  }
  const t30 = applied_filters as BulkOperationFilters;
  let t31;
  if ($[89] !== t30) {
    t31 = <BulkRequestActionDropdown filters={t30} />;
    $[89] = t30;
    $[90] = t31;
  } else {
    t31 = $[90];
  }
  const t32 = list_data.is_loading ? "loading" : "reload";
  const t33 = list_data.is_loading || !filters.date?.relative;
  let t34;
  if ($[91] !== is_loading_metrics || $[92] !== list_data || $[93] !== refetch || $[94] !== updateSearchQuery) {
    t34 = () => {
      updateSearchQuery({}, {
        remove_keys: ["prev", "next", "selected_request_id", "selected_entry_id", "current_page"]
      });
      list_data.refresh();
      setRenderKey(Date.now());
      if (!is_loading_metrics) {
        refetch();
      }
    };
    $[91] = is_loading_metrics;
    $[92] = list_data;
    $[93] = refetch;
    $[94] = updateSearchQuery;
    $[95] = t34;
  } else {
    t34 = $[95];
  }
  let t35;
  if ($[96] !== filters.date?.relative || $[97] !== list_data.has_next_results) {
    t35 = list_data.has_next_results && filters.date?.relative && <StatusDot color="primary" m={{
      l: 2
    }} />;
    $[96] = filters.date?.relative;
    $[97] = list_data.has_next_results;
    $[98] = t35;
  } else {
    t35 = $[98];
  }
  let t36;
  if ($[99] !== t32 || $[100] !== t33 || $[101] !== t34 || $[102] !== t35) {
    t36 = <Button icon={t32} outline={true} disabled={t33} onClick={t34}>Refresh{t35}</Button>;
    $[99] = t32;
    $[100] = t33;
    $[101] = t34;
    $[102] = t35;
    $[103] = t36;
  } else {
    t36 = $[103];
  }
  let t37;
  if ($[104] !== SaveViewButton) {
    t37 = <FiltersContext.Consumer>{t38 => {
        const {
          values: values_2
        } = t38;
        return <SaveViewButton values={values_2} />;
      }}</FiltersContext.Consumer>;
    $[104] = SaveViewButton;
    $[105] = t37;
  } else {
    t37 = $[105];
  }
  let t38;
  if ($[106] !== t31 || $[107] !== t36 || $[108] !== t37) {
    t38 = <StyledViewNavActions>{t31}{t36}{t37}</StyledViewNavActions>;
    $[106] = t31;
    $[107] = t36;
    $[108] = t37;
    $[109] = t38;
  } else {
    t38 = $[109];
  }
  let t39;
  if ($[110] !== t29 || $[111] !== t38) {
    t39 = <StyledViewNavHeader>{t29}{t38}</StyledViewNavHeader>;
    $[110] = t29;
    $[111] = t38;
    $[112] = t39;
  } else {
    t39 = $[112];
  }
  let t40;
  let t41;
  if ($[113] === Symbol.for("react.memo_cache_sentinel")) {
    t40 = {
      align: "flex-start",
      gap: 3
    };
    t41 = {
      x: 6,
      y: 3
    };
    $[113] = t40;
    $[114] = t41;
  } else {
    t40 = $[113];
    t41 = $[114];
  }
  let t42;
  if ($[115] !== filters || $[116] !== setDate || $[117] !== stats_end_date || $[118] !== stats_start_date) {
    t42 = <DateFilter filters={filters} setDate={setDate} start_date={stats_start_date} end_date={stats_end_date} />;
    $[115] = filters;
    $[116] = setDate;
    $[117] = stats_end_date;
    $[118] = stats_start_date;
    $[119] = t42;
  } else {
    t42 = $[119];
  }
  let t43;
  if ($[120] !== search_term_input || $[121] !== submitSearchTerm) {
    t43 = () => submitSearchTerm(search_term_input);
    $[120] = search_term_input;
    $[121] = submitSearchTerm;
    $[122] = t43;
  } else {
    t43 = $[122];
  }
  const t44 = !!search_term_input && search_term_input === (filters.search_term || "");
  let t45;
  if ($[123] === Symbol.for("react.memo_cache_sentinel")) {
    t45 = {
      px: 250
    };
    $[123] = t45;
  } else {
    t45 = $[123];
  }
  let t46;
  if ($[124] !== search_term_input || $[125] !== submitSearchTerm || $[126] !== t43 || $[127] !== t44) {
    t46 = <Search search_ref={search_input_ref} onChange={onSearchTermInputChange} onSubmit={submitSearchTerm} onBlur={t43} value={search_term_input} submitted={t44} placeholder="Search payload fields" small={true} add_keybinds={true} min_w={t45} />;
    $[124] = search_term_input;
    $[125] = submitSearchTerm;
    $[126] = t43;
    $[127] = t44;
    $[128] = t46;
  } else {
    t46 = $[128];
  }
  let t47;
  if ($[129] === Symbol.for("react.memo_cache_sentinel")) {
    t47 = <Divider vertical={true} />;
    $[129] = t47;
  } else {
    t47 = $[129];
  }
  let t48;
  if ($[130] === Symbol.for("react.memo_cache_sentinel")) {
    t48 = <Div w={100}><Filters.Form /></Div>;
    $[130] = t48;
  } else {
    t48 = $[130];
  }
  let t49;
  if ($[131] !== t42 || $[132] !== t46) {
    t49 = <StyledViewNavSecondary p={0}><Div flex={t40} p={t41}>{t42}{t46}{t47}{t48}</Div></StyledViewNavSecondary>;
    $[131] = t42;
    $[132] = t46;
    $[133] = t49;
  } else {
    t49 = $[133];
  }
  let t50;
  if ($[134] !== t39 || $[135] !== t49) {
    t50 = <StyledViewNav>{t39}{t49}</StyledViewNav>;
    $[134] = t39;
    $[135] = t49;
    $[136] = t50;
  } else {
    t50 = $[136];
  }
  const t51 = active_table ? "calc(100vh - 159px)" : "calc(100vh - 106px)";
  let t52;
  if ($[137] !== t51) {
    t52 = {
      overflow: "auto",
      height: t51,
      position: "relative"
    };
    $[137] = t51;
    $[138] = t52;
  } else {
    t52 = $[138];
  }
  let t53;
  if ($[139] === Symbol.for("react.memo_cache_sentinel")) {
    t53 = {
      direction: "column"
    };
    $[139] = t53;
  } else {
    t53 = $[139];
  }
  let t54;
  if ($[140] === Symbol.for("react.memo_cache_sentinel")) {
    t54 = {
      x: 6,
      y: 3
    };
    $[140] = t54;
  } else {
    t54 = $[140];
  }
  let t55;
  if ($[141] === Symbol.for("react.memo_cache_sentinel")) {
    t55 = <Divider />;
    $[141] = t55;
  } else {
    t55 = $[141];
  }
  let t56;
  if ($[142] !== list_data.requests) {
    t56 = list_data.requests.length > 0 && <><Div flex={{
        align: "flex-start",
        gap: 3
      }} p={{
        x: 6,
        y: 3
      }}><Div flex={{
          align: "center",
          gap: 2
        }} h={{
          px: 28
        }} m={{
          r: 4.5
        }}><Icon icon="add_column_right" muted={true} /><Text semi={true} muted={true} text_wrap={false}>Suggested columns</Text></Div><Divider vertical={true} /><Div w={100}><SuggestedColumns entity="requests" models={list_data.requests} /></Div></Div><Divider /></>;
    $[142] = list_data.requests;
    $[143] = t56;
  } else {
    t56 = $[143];
  }
  let t57;
  if ($[144] !== current_page || $[145] !== entries_list || $[146] !== filters || $[147] !== handleRetry || $[148] !== has_slow_request_rate || $[149] !== has_sources || $[150] !== is_loading_metrics || $[151] !== list_data || $[152] !== next_relative_date || $[153] !== onEntrySelected || $[154] !== onPaginationChanged || $[155] !== onRequestSelected || $[156] !== refetch || $[157] !== selected_entry_id || $[158] !== selected_request_id || $[159] !== setDate || $[160] !== should_show_list_loading || $[161] !== stats_end_date || $[162] !== stats_start_date || $[163] !== theme || $[164] !== total_count || $[165] !== updateSearchQuery) {
    t57 = !has_sources && !list_data.requests.length && !should_show_list_loading ? <StyledEmptyStateWrapper><EmptyState title="Inspect inbound HTTP requests" description="Requests represent inbound HTTP requests to Hookdeck from external services (sources). When we route them to destinations, we create an event. Configure a connection to view requests." asset={`requests-${theme.mode}.png`} asset_position="background" cta={{
        label: "Create first connection",
        icon: "connections",
        to: "/create-first-connection"
      }} /></StyledEmptyStateWrapper> : list_data.requests.length === 0 && Object.keys(filters).length && list_data.fetched && !should_show_list_loading ? <StyledEmptyStateWrapper><EmptyResults label="No matching requests found" cta={next_relative_date ? <DateFilter filters={filters} setDate={setDate} start_date={stats_start_date} end_date={stats_end_date} /> : undefined} /></StyledEmptyStateWrapper> : <ErrorBoundary><RequestsList {...list_data} fetched={should_show_list_loading ? false : list_data.fetched} entries_list={entries_list} pagination={{
        next: list_data.next || undefined,
        prev: list_data.prev || undefined,
        page_size: 100,
        current_page
      }} retry={handleRetry} filters={filters} onEntrySelected={onEntrySelected} onRequestSelected={onRequestSelected} selected_entry_id={selected_entry_id} selected_request_id={selected_request_id} onPaginationChanged={onPaginationChanged} total_count={total_count} has_new_results={list_data.has_next_results && has_slow_request_rate} onRefresh={() => {
        updateSearchQuery({}, {
          remove_keys: ["prev", "next", "selected_request_id", "selected_entry_id", "current_page"]
        });
        list_data.refresh();
        setRenderKey(Date.now());
        if (!is_loading_metrics) {
          refetch();
        }
      }} /></ErrorBoundary>;
    $[144] = current_page;
    $[145] = entries_list;
    $[146] = filters;
    $[147] = handleRetry;
    $[148] = has_slow_request_rate;
    $[149] = has_sources;
    $[150] = is_loading_metrics;
    $[151] = list_data;
    $[152] = next_relative_date;
    $[153] = onEntrySelected;
    $[154] = onPaginationChanged;
    $[155] = onRequestSelected;
    $[156] = refetch;
    $[157] = selected_entry_id;
    $[158] = selected_request_id;
    $[159] = setDate;
    $[160] = should_show_list_loading;
    $[161] = stats_end_date;
    $[162] = stats_start_date;
    $[163] = theme;
    $[164] = total_count;
    $[165] = updateSearchQuery;
    $[166] = t57;
  } else {
    t57 = $[166];
  }
  let t58;
  if ($[167] !== current_page || $[168] !== has_request_filter || $[169] !== list_data.count || $[170] !== list_data.fetched || $[171] !== list_data.is_loading || $[172] !== list_data.next || $[173] !== list_data.prev || $[174] !== list_data.requests.length || $[175] !== total_count || $[176] !== updateSearchQuery) {
    t58 = list_data.requests.length > 0 && list_data.fetched && <TableFooter fill={true} current_count={list_data.count} total_count={has_request_filter ? undefined : total_count} loading={list_data.is_loading} pagination={{
      current_page,
      page_size: 100,
      entity_name: "requests"
    }} onPreviousPage={current_page > 1 ? () => {
      updateSearchQuery({
        prev: list_data.prev || undefined,
        next: undefined,
        current_page: String(current_page - 1)
      });
    } : undefined} onNextPage={list_data.next ? () => {
      updateSearchQuery({
        next: list_data.next,
        prev: undefined,
        current_page: String(current_page + 1)
      });
    } : undefined} />;
    $[167] = current_page;
    $[168] = has_request_filter;
    $[169] = list_data.count;
    $[170] = list_data.fetched;
    $[171] = list_data.is_loading;
    $[172] = list_data.next;
    $[173] = list_data.prev;
    $[174] = list_data.requests.length;
    $[175] = total_count;
    $[176] = updateSearchQuery;
    $[177] = t58;
  } else {
    t58 = $[177];
  }
  let t59;
  if ($[178] !== handleOnNext || $[179] !== handleOnPrev || $[180] !== handleRetry || $[181] !== request_1 || $[182] !== selected_entry_id || $[183] !== selected_request_id || $[184] !== updateSearchQuery) {
    t59 = selected_request_id && (selected_entry_id ? <EntrySidebar request_id={selected_request_id} entry_id={selected_entry_id} retry={(request_id_0, webhook_ids_0) => handleRetry(request_id_0, webhook_ids_0).then(t60 => {
      const {
        events: events_0
      } = t60;
      const event = events_0.find(e_0 => e_0.webhook_id === webhook_ids_0[0]);
      if (event) {
        updateSearchQuery({
          selected_entry_id: event.id
        });
      }
    })} onClose={() => {
      updateSearchQuery({}, {
        remove_keys: ["selected_request_id", "selected_entry_id"]
      });
    }} /> : <RequestSidebar list_request={request_1} request_id={selected_request_id} onClose={() => {
      updateSearchQuery({}, {
        remove_keys: ["selected_request_id"]
      });
    }} onPrev={handleOnPrev} onNext={handleOnNext} />);
    $[178] = handleOnNext;
    $[179] = handleOnPrev;
    $[180] = handleRetry;
    $[181] = request_1;
    $[182] = selected_entry_id;
    $[183] = selected_request_id;
    $[184] = updateSearchQuery;
    $[185] = t59;
  } else {
    t59 = $[185];
  }
  return <TableProvider initialConfig={t26} has_scrolled={has_scrolled}><Filters.Provider route="requests" filters={filters} components={filter_components} onFilterChanged={onFilterChanged}><StyledViewLayout><StyledViewContent>{t50}<Div ref={scrolled_element_ref} style={t52} flex={t53}><StyledCentered><ErrorBoundary><Div p={t54}><Histogram dimention="rejection_cause" model="Requests" key={list_render_key} start_date={stats_start_date} end_date={stats_end_date} filters={applied_filters} setDate={setDate} refresh_key={latest_request ? latest_request.created_at : undefined} /></Div></ErrorBoundary>{t55}{t56}</StyledCentered>{t57}</Div>{t58}</StyledViewContent>{t59}</StyledViewLayout></Filters.Provider></TableProvider>;
};
export default RequestsView;
function _temp(v_0) {
  return !!v_0;
}
function _temp2(v_1) {
  return v_1;
}